/**
 * Plan 23/24 — Dexie-backed IndexedDB cache for offline-first Cafeteria
 * Mode.
 *
 * Five tables back the offline shell:
 *
 *   - `unservedBuckets` — mirror of the latest `/api/cafeteria/unserved-buckets`
 *     response so the BucketGrid can render from cache when the kiosk
 *     is offline. Keyed by a composite id (`{locationId}::{mealtime}::{menuItemId}::{gradeRangeId}::{rosterName}`).
 *   - `students` — full school roster for student-search offline.
 *     Indexed on the lowercase concat `searchText` so prefix matching
 *     is O(log n). PR6 adds last-known POS wallet fields (advisory
 *     while offline).
 *   - `posMenuItems` — cached POS register menu (PROD-227 PR6) so the
 *     sale sheet keeps ringing offline. Keyed by
 *     `{locationId}::{mealtime}::{menuItemId}`.
 *   - `syncQueue` — pending mutations that ran while offline. Plan 24
 *     drains them via the replay manager.
 *   - `failedSyncQueue` — dead-letter queue (Plan 24). Rows arrive here
 *     after the replay manager has exhausted all `BACKOFFS_MS` retries
 *     on a row. SCHOOL_ADMIN can replay or discard from Plan 25's
 *     Devices section.
 *   - `meta` — kiosk session token reference, last-sync timestamps,
 *     storage-quota state ("ok" / "warn" / "critical" / "emergency").
 *
 * Schema-rebuild policy (per the canonical plan):
 *   - A schema bump (`this.version(N).stores(...)`) drops every existing
 *     row and re-hydrates from the server on next load. The 2-5 second
 *     "Loading roster data..." indicator is acceptable — daily reset
 *     already happens at the start of service.
 *   - A failed migration falls back to a clean empty DB rather than
 *     entering a half-migrated state. The hard reset is exposed via
 *     `resetCafeteriaDb()` so the bootstrap flow can recover.
 *
 * Single-tab assumption: kiosks aren't multi-tab. If multi-tab support
 * is later required, wrap writes in a BroadcastChannel-coordinated
 * lock; the current implementation deliberately skips that for
 * simplicity.
 */

import type { Table } from 'dexie'
import Dexie from 'dexie'

import type { StudentAllergenWire } from '../api/cafeteria'

// ---------------------------------------------------------------------------
// Wire-shaped row interfaces
// ---------------------------------------------------------------------------

export interface UnservedBucketRow {
  /**
   * Composite id — stable across syncs so a bucket whose count changed
   * still overwrites the same row.
   *
   *   `{locationId}::{mealtime}::{menuItemId}::{gradeRangeId ?? '-'}::{rosterName ?? '-'}`
   */
  id: string
  locationId: string
  mealtime: string
  menuItemId: string
  menuItemName: string
  gradeRangeId: string | null
  gradeRangeName: string | null
  /**
   * PROD-1122 — band identity + bounds for the shared pool-compliance
   * rule (`poolComplianceTier` in @ordo/constants/grade-ladder), so the
   * offline register can decide which cached buckets may serve a
   * student. Nullable: bandless buckets sync nulls.
   */
  gradeRangeCode: string | null
  minGrade: string | null
  maxGrade: string | null
  /** The bucket's group axis (HOMEROOM etc.); AGE_GROUP follows the band rule. */
  groupDimension: string | null
  groupValue: string | null
  /** Homeroom/group display name for group-bucketed items. */
  rosterName: string | null
  /** SUM of quantities — counts MEALS, not rows (PROD-1122 fix). */
  count: number
  sampleIds: string[]
  /** Epoch ms when the row was last written from server. */
  syncedAt: number
}

export interface StudentRow {
  id: string
  schoolId: string
  firstName: string
  lastName: string
  /** Display-only data; not indexed, so existing caches need no schema migration. */
  allergens?: StudentAllergenWire[]
  /**
   * Full name is written family name first (PROD-1107). Not indexed, so it
   * needs no Dexie version bump — `searchText` still indexes both parts.
   */
  familyNameFirst?: boolean
  gradeRangeId: string | null
  barcode: string | null
  /**
   * The school's own student number (`Membership.localStudentId`). Display
   * only, and NOT indexed, so it needs no Dexie version bump — the numeric
   * `barcode` index still owns scanner lookup. Null on rows cached before
   * this shipped.
   */
  localStudentId?: string | null
  /**
   * Lowercase `${firstName} ${lastName}` — used for prefix matching via
   * Dexie's index. Recomputed at sync time, not at search time.
   */
  searchText: string
  /**
   * PROD-1122 — pool-compliance facts (grade fact, explicit band code,
   * homeroom roster value), synced with the roster so the offline
   * register can count compliant pre-order units. All nullable.
   */
  grade?: string | null
  gradeRangeCode?: string | null
  rosterName?: string | null
  /**
   * PROD-227 PR6 — last-known POS wallet facts, synced with the roster.
   * All nullable: non-POS schools sync nulls, and the values are
   * ADVISORY ONLY offline (server math is authoritative at replay —
   * a cached cap/floor never blocks ringing an offline sale).
   */
  posOptIn?: boolean | null
  balanceCents?: number | null
  spendingLimitCents?: number | null
  remainingDailyCapCents?: number | null
  walletLocked?: boolean | null
  /** Payer household has wallet auto-charge on (PROD-1119) — display only. */
  autoReloadOn?: boolean | null
}

/**
 * PROD-227 PR6 — cached POS register menu (one row per sellable item
 * per location+mealtime). Hydrated from `GET /api/cafeteria/pos/menu`
 * on the bucket-sync cadence so the register keeps ringing offline.
 */
export interface PosMenuItemRow {
  /** `{locationId}::{mealtime}::{menuItemId}` — stable across syncs. */
  id: string
  locationId: string
  mealtime: string
  menuItemId: string
  /** Chef-swap-overlaid display name (resolved server-side at sync). */
  name: string
  priceCents: number
  /** PROD-1278 — sold out at the serve line when last synced (v6). */
  soldOut?: boolean
  /**
   * The owning menu's grade-band tags (empty = every band). The cache holds
   * the CAMPUS-WIDE menu; the offline register gates rows per student with
   * `menuAllowsGradeRangeCode` at read time (v7).
   */
  gradeRangeCodes?: string[]
  /** Epoch ms when the row was last written from server. */
  syncedAt: number
}

export type SyncQueueOperation =
  | 'serveAnonymous'
  | 'serveToStudent'
  | 'voidUnassigned'
  | 'serveAnonymousBatch'
  | 'posSale'
  /** PROD-1278 — one school-tally tap (+N / −N) on a production-record bucket. */
  | 'serveTallyDelta'

export interface SyncQueueRow {
  /**
   * Local UUID; persists across restarts. Plan 24's replay manager
   * uses this as the idempotency key when retrying against the server.
   */
  id: string
  operation: SyncQueueOperation
  variables: Record<string, unknown>
  /**
   * Optimistic patch applied locally — used by Plan 24 to revert when
   * the server permanently rejects the row. Stored as opaque JSON.
   */
  optimisticPatch: unknown
  createdAt: number
  attempts: number
  lastAttemptAt: number | null
  lastError: string | null
}

/**
 * Dead-letter row (Plan 24). Mirrors the live `SyncQueueRow` shape
 * with two extras:
 *
 *   - `deadAt` — epoch ms when the replay manager gave up. SCHOOL_ADMIN
 *     uses this to triage "how stale are these?" before deciding to
 *     retry or discard.
 *   - `lastError` — the final error message the replay manager saw.
 *     Surfaced in Plan 25's Devices section so the human investigator
 *     can pattern-match against incidents.
 *
 * Plan 25's `FailedReplayBanner` reads from this table; the buttons
 * either move rows back to `syncQueue` (retry path) or call `.clear()`
 * (discard path — the paper backup is authoritative at that point).
 */
export interface FailedSyncQueueRow {
  id: string
  operation: SyncQueueOperation
  variables: Record<string, unknown>
  optimisticPatch: unknown
  /** Original `createdAt` from when the row was first enqueued offline. */
  createdAt: number
  /** Total attempts including the final one that triggered dead-letter. */
  attempts: number
  /** Epoch ms when the last attempt fired (carries over from `syncQueue`). */
  lastAttemptAt: number | null
  lastError: string | null
  /** Epoch ms when the replay manager moved the row here. */
  deadAt: number
}

export interface MetaRow {
  key: string
  value: unknown
}

// ---------------------------------------------------------------------------
// Database class
// ---------------------------------------------------------------------------

export class CafeteriaDb extends Dexie {
  unservedBuckets!: Table<UnservedBucketRow, string>
  students!: Table<StudentRow, string>
  posMenuItems!: Table<PosMenuItemRow, string>
  syncQueue!: Table<SyncQueueRow, string>
  failedSyncQueue!: Table<FailedSyncQueueRow, string>
  meta!: Table<MetaRow, string>

  constructor(name = DB_NAME) {
    super(name)
    // Compound indexes:
    //   - `[locationId+mealtime]` — bucket/POS-menu query by location+mealtime.
    //   - `[schoolId+searchText]` — roster scan filtered by school.
    //   - `barcode` — scanner lookup by ID-card barcode.
    //   - `createdAt` / `attempts` — sync replay ordering.
    //   - `operation` on `syncQueue` — the POS pending-charges pill counts
    //     `posSale` rows without scanning the whole queue.
    //   - `deadAt` on `failedSyncQueue` — newest-first for the banner UI.
    this.version(SCHEMA_VERSION).stores({
      unservedBuckets: 'id, locationId, mealtime, [locationId+mealtime]',
      students: 'id, schoolId, barcode, searchText, [schoolId+searchText]',
      posMenuItems: 'id, locationId, mealtime, [locationId+mealtime]',
      syncQueue: 'id, createdAt, attempts, operation',
      failedSyncQueue: 'id, createdAt, deadAt',
      meta: 'key',
    })
  }
}

/**
 * Dexie database name. Kept stable across schema versions so the daily
 * reset doesn't change the database name (only the schema version).
 */
export const DB_NAME = 'OrdoCafeteria'

/**
 * Current schema version. Bumping this drops every existing row on next
 * open per the canonical cache-rebuild design. Do NOT add incremental
 * migration logic — let it rebuild from server hydration.
 *
 * History:
 *   - v1 (Plan 23) — unservedBuckets / students / syncQueue / meta
 *   - v2 (Plan 24) — added failedSyncQueue (replay manager dead-letter)
 *   - v3 (roster rebuild Phase 7) — bucket rows keyed by group name, not
 *     roster id
 *   - v4 (PROD-227 PR6) — StudentRow gains last-known POS wallet fields;
 *     new posMenuItems table; `operation` index on syncQueue for the
 *     pending-charges pill
 *   - v5 (PROD-1122) — bucket rows gain band identity/bounds +
 *     quantity-summed count; student rows gain grade/band/roster for
 *     pool compliance.
 *   - v6 (PROD-1278) — POS menu rows carry the serve line's `soldOut`
 *     flag so the offline register keeps sold-out rows disabled.
 *   - v7 — POS menu rows carry the menu's `gradeRangeCodes` so the offline
 *     register applies the student grade gate; rows cached without the
 *     field would read as untagged (every band), so the cache rebuilds.
 */
export const SCHEMA_VERSION = 7

let dbInstance: CafeteriaDb | null = null

/**
 * Singleton accessor. Creates the Dexie connection lazily on first
 * call (so tests / SSR that never touch the DB pay no cost).
 *
 * Test helpers re-instantiate by calling `resetCafeteriaDb()` first;
 * production code uses this directly.
 */
export function getCafeteriaDb(): CafeteriaDb {
  if (!dbInstance) {
    dbInstance = new CafeteriaDb()
  }
  return dbInstance
}

/**
 * Hard-reset the cache. Closes the current connection, deletes the
 * underlying IndexedDB database, and reopens a fresh one against the
 * current schema. Used by:
 *
 *   - The bootstrap flow when a Dexie migration fails (the failure
 *     handler falls back to a clean empty DB rather than a half-
 *     migrated state).
 *   - SCHOOL_ADMIN re-pair (Plan 25) when storage quota can't be
 *     recovered any other way.
 *   - Tests that need a clean slate per case.
 *
 * Resolves once the new connection is open.
 */
export async function resetCafeteriaDb(): Promise<void> {
  if (dbInstance) {
    dbInstance.close()
    dbInstance = null
  }
  // `Dexie.delete` resolves even when no DB exists yet — safe to call
  // on a fresh kiosk that's never opened the cache.
  await Dexie.delete(DB_NAME)
  dbInstance = new CafeteriaDb()
  // Trigger the open by calling `.open()` explicitly — Dexie otherwise
  // defers opening until the first query, which complicates `await`
  // semantics in callers.
  await dbInstance.open()
}

/**
 * Compose the composite bucket id. Stable across syncs so a bucket
 * whose count changed overwrites the same row.
 *
 * Pure — exported for tests.
 */
export function bucketRowId(args: {
  locationId: string
  mealtime: string
  menuItemId: string
  gradeRangeId: string | null
  rosterName: string | null
}): string {
  return `${args.locationId}::${args.mealtime}::${args.menuItemId}::${args.gradeRangeId ?? '-'}::${args.rosterName ?? '-'}`
}

/**
 * Lowercase concatenation used as the student-search index. Pure —
 * exported for tests + reused by the sync layer.
 */
export function studentSearchText(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.toLowerCase().trim()
}

/**
 * Compose the composite POS-menu row id — stable across syncs so a
 * price/name change overwrites the same record. Pure — exported for
 * tests + the sync layer.
 */
export function posMenuRowId(args: { locationId: string; mealtime: string; menuItemId: string }): string {
  return `${args.locationId}::${args.mealtime}::${args.menuItemId}`
}
